import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Box, Typography, Modal, Button, CircularProgress } from '@mui/material';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { areaElementClasses } from '@mui/x-charts/LineChart';
import { useGrowSetup }  from '../../GrowSetupContext';
import { AuthContext }   from '../../components/signin/AuthContext';
import { useConfig }     from '../../components/ConfigContext';
import SmallConfigTemperatur from '../../components/SmallConfigTemperatur';
import SmallConfigLicht      from '../../components/SmallConfigLicht';
import SmallConfigAbluft     from '../../components/SmallConfigAbluft';
import SmallConfigCo2        from '../../components/SmallConfigCo2';
import ArcGauge from './ArcGauge';
import { useV4Resolve } from '../theme';

const MAX_BY_MEASURE = {
  temperatur: 50, temperatur_2: 50,
  humidity: 100,  humidity_2: 100,
  co2: 2000,
  vpd: 3,
  luftdruck: 1100,
  bodenfeuchte_1: 100, bodenfeuchte_2: 100, bodenfeuchte_3: 100, bodenfeuchte_4: 100,
  bme: 1, sht: 1,
};

function AreaGradient({ color, id }) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%"   stopColor={color} stopOpacity={0.4} />
        <stop offset="100%" stopColor={color} stopOpacity={0}   />
      </linearGradient>
    </defs>
  );
}

export default function SensorGauge({ measure, title, color: colorProp, unit, ideal_val, gauge_value, animDelay = 0 }) {
  const color = useV4Resolve()(colorProp);
  const { grow_setup } = useGrowSetup();
  const { token }      = useContext(AuthContext);
  const { state: cfgState } = useConfig();
  const configReady = typeof cfgState.config.sun_rise === 'string';

  const [chart,   setChart]   = useState([]);
  const [xaxis,   setXaxis]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [open,    setOpen]    = useState(false);
  const gradId = useMemo(() => `v4-sg-${measure}`, [measure]);

  useEffect(() => {
    if (!grow_setup) return;
    let cancelled = false;
    const end = new Date(), start = new Date(end - 24 * 3600 * 1000);
    axios.post('/get_measures_by_timedelta/', {
      start_time: start, end_time: end,
      grow_setup_id: grow_setup, aggregation_level: '5m', measure,
    }, {
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    }).then(res => {
      if (cancelled) return;
      const row  = res?.data?.[0];
      const data = row?.data  ?? [];
      const rawX = row?.xaxis ?? [];
      const fmt  = ts => { const d = new Date(ts); return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; };
      setChart(data); setXaxis(rawX.map(fmt));
    }).catch(console.error)
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [grow_setup, measure]);

  const max = MAX_BY_MEASURE[measure] ?? 100;

  // Status based on deviation
  let statusLabel = 'unbekannt', statusColor = 'var(--v4-neutral)';
  if (ideal_val != null && gauge_value != null && Number(gauge_value) !== 0) {
    const dev = Math.abs(Number(gauge_value) - Number(ideal_val)) / Math.abs(Number(ideal_val));
    if (dev < 0.1)      { statusLabel = 'Optimal';   statusColor = 'var(--v4-green)'; }
    else if (dev < 0.2) { statusLabel = 'Abweichend'; statusColor = 'var(--v4-orange)'; }
    else                { statusLabel = 'Kritisch';   statusColor = 'var(--v4-red)'; }
  }

  return (
    <>
      <Box
        onClick={() => setOpen(true)}
        sx={{
          background: 'rgba(var(--v4-surface-rgb),0.9)',
          border: `1px solid color-mix(in srgb, ${color} 13%, transparent)`,
          borderRadius: '20px',
          backdropFilter: 'blur(20px)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 2.5,
          cursor: 'pointer',
          animation: `v4FadeUp 0.55s cubic-bezier(0.22,1,0.36,1) ${animDelay}ms both`,
          transition: 'transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease',
          '&:hover': {
            transform: 'translateY(-4px) scale(1.02)',
            border: `1px solid color-mix(in srgb, ${color} 33%, transparent)`,
            boxShadow: `0 12px 40px color-mix(in srgb, ${color} 13%, transparent)`,
          },
          position: 'relative',
        }}
      >
        {/* Corner label */}
        <Typography
          variant="caption"
          sx={{
            position: 'absolute', top: 14, left: 16,
            color: color, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.1em',
            fontSize: '0.62rem',
          }}
        >
          {title}
        </Typography>

        {/* Status chip */}
        <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
          <Box sx={{
            width: 8, height: 8, borderRadius: '50%',
            background: statusColor,
            boxShadow: `0 0 8px ${statusColor}`,
            animation: statusColor === 'var(--v4-green)' ? 'v4Pulse 2s infinite' : 'none',
          }} />
        </Box>

        {/* Gauge */}
        <Box sx={{ mt: 3, mb: 1 }}>
          <ArcGauge
            value={gauge_value}
            max={max}
            ideal={ideal_val}
            unit={unit}
            color={color}
            size={130}
          />
        </Box>

        {/* Sparkline */}
        {!loading && chart.length > 0 && (
          <Box sx={{ width: '100%', height: 40, mt: 0.5 }}>
            <SparkLineChart
              data={chart}
              xAxis={{ scaleType: 'band', data: xaxis }}
              colors={[color]}
              area showHighlight showTooltip
              sx={{
                [`& .${areaElementClasses.root}`]: { fill: `url(#${gradId})` },
                '& .MuiChartsAxis-root': { display: 'none' },
              }}
            >
              <AreaGradient color={color} id={gradId} />
            </SparkLineChart>
          </Box>
        )}

        {/* Ideal hint */}
        {ideal_val != null && (
          <Typography variant="caption" sx={{ color: 'rgba(var(--v4-muted-rgb),0.4)', mt: 0.5, fontSize: '0.67rem' }}>
            Ziel: {ideal_val} {unit}
          </Typography>
        )}
      </Box>

      {/* Detail modal */}
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%,-50%)',
          width: 440, maxWidth: '92vw',
          background: 'rgba(var(--v4-surface2-rgb),0.97)',
          border: `1px solid color-mix(in srgb, ${color} 20%, transparent)`,
          borderRadius: '20px',
          backdropFilter: 'blur(24px)',
          boxShadow: `0 32px 80px rgba(var(--v4-shadow-rgb),calc(0.7*var(--v4-shadow-k))), 0 0 60px color-mix(in srgb, ${color} 7%, transparent)`,
          p: 3,
        }}>
          {!configReady && (
            <Box sx={{ display:'flex', justifyContent:'center', py:6 }}>
              <CircularProgress size={28} sx={{ color:'var(--v4-cyan)' }} />
            </Box>
          )}
          {configReady && measure === 'licht'      && <SmallConfigLicht />}
          {configReady && measure === 'humidity'   && <SmallConfigAbluft />}
          {configReady && (measure === 'temperatur' || measure === 'temperatur_2') && <SmallConfigTemperatur />}
          {configReady && measure === 'co2'        && <SmallConfigCo2 />}
          {configReady && !['licht','humidity','temperatur','temperatur_2','co2'].includes(measure) && (
            <Typography sx={{ color: 'rgba(var(--v4-text-rgb),0.5)', textAlign: 'center', py: 3 }}>
              Keine Detailkonfiguration verfügbar.
            </Typography>
          )}
          <Button fullWidth variant="outlined"
            onClick={() => setOpen(false)}
            sx={{ mt: 2, borderColor: `color-mix(in srgb, ${color} 20%, transparent)`, color: 'rgba(var(--v4-text-rgb),0.5)', '&:hover': { borderColor: color } }}
          >
            Schließen
          </Button>
        </Box>
      </Modal>

      <style>{`
        @keyframes v4FadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        @keyframes v4Pulse  { 0%,100%{box-shadow:0 0 0 0 color-mix(in srgb, var(--v4-green) 53%, transparent)} 50%{box-shadow:0 0 0 6px color-mix(in srgb, var(--v4-green) 0%, transparent)} }
      `}</style>
    </>
  );
}

SensorGauge.propTypes = {
  measure:     PropTypes.string.isRequired,
  title:       PropTypes.string,
  color:       PropTypes.string,
  unit:        PropTypes.string,
  ideal_val:   PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  gauge_value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  animDelay:   PropTypes.number,
};
